import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { flatNavItems } from '@/components/layout/nav.config';
import { useClientFlags } from '@/config/client-flags';
import { hasFeature } from '@/config/features';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useUsagePages,
  type Subject,
  type UsageFilters,
} from './usage-analytics.api';
import { Empty, ErrorNotice } from './usage-parts';

/**
 * Pages of this portal that nobody, or almost nobody, opens.
 *
 * The one panel that is about the portal rather than about the subjects: it
 * reads everybody's visits, whoever is being compared above. A page can only
 * be called unused by everyone, and the page list it is checked against is
 * this app's own navigation, which is the only app whose pages are known here.
 *
 * It is a list of candidates, not verdicts. A page three people are allowed to
 * open will never be busy, and one used once a quarter is quiet in any thirty
 * days; the help line says to read it over a long range.
 */

/** The portal's own app, the only one whose page list this build knows. */
const OWN_DOMAIN = 'admin-portal';

/** Fewer visits than this in the range and a page is listed. */
const RARE = 5;

const TOP = 100;

const EVERYONE: Subject[] = [{ kind: 'group', key: 'unused', slot: 0 }];

export function UnusedPages({ filters }: { filters: UsageFilters }) {
  const { t } = useTranslation();
  const flags = useClientFlags();

  // Pinned to this app whatever the filter says: with "all apps" selected the
  // hundred busiest pages would be shared with every other app, and a portal
  // page crowded out of that list would be reported as never opened.
  const own = useMemo(() => ({ ...filters, domain: OWN_DOMAIN }), [filters]);
  const pages = useUsagePages(own, EVERYONE, TOP);

  const rows = useMemo(() => {
    const opened = pages.data?.[0]?.pages.filter((page) => page.visits > 0) ?? [];
    const byKey = new Map(opened.map((page) => [page.page, page]));
    // A full list may have been cut before reaching a page that was opened.
    const cut = opened.length >= TOP;

    return flatNavItems
      .filter(
        (item) =>
          // Only what a visit could have been recorded for: built pages, not
          // Home (the recorder skips it), and not modules this client does
          // not have — those are absent, not unused.
          item.status === 'built' &&
          item.to !== '/' &&
          hasFeature(item.feature) &&
          (!item.flag || flags[item.flag])
      )
      .flatMap((item) => {
        const hit = byKey.get(item.key);
        if (!hit && cut) return [];
        const visits = hit?.visits ?? 0;
        if (visits >= RARE) return [];
        return [{ item, visits, users: hit?.users ?? 0 }];
      })
      .sort((a, b) => a.visits - b.visits);
  }, [pages.data, flags]);

  if (pages.isError) {
    return (
      <ErrorNotice
        message={pages.error?.message || t('usage.loadFailed')}
        onRetry={() => void pages.refetch()}
      />
    );
  }
  if (pages.isLoading) return <Skeleton className="h-40 rounded-xl" />;
  if (rows.length === 0) {
    return <Empty>{t('usage.noUnused', { count: RARE })}</Empty>;
  }

  return (
    <ul
      className={cn(
        'bg-card divide-y rounded-xl border',
        pages.isFetching && 'opacity-60 transition-opacity'
      )}
    >
      {rows.map(({ item, visits, users }) => (
        <li key={item.key} className="flex items-baseline gap-3 px-3 py-2">
          <span className="min-w-0 flex-1">
            <span className="text-sm font-medium">{t(item.label)}</span>
            {item.parentLabel && (
              <span className="text-muted-foreground ml-2 text-xs">
                {t(item.parentLabel)}
              </span>
            )}
          </span>
          <span
            className={cn(
              'shrink-0 text-sm tabular-nums',
              visits > 0 && 'text-muted-foreground'
            )}
          >
            {visits === 0
              ? t('usage.neverOpened')
              : t('usage.pageCell', {
                  visits: formatNumber(visits),
                  users: formatNumber(users),
                })}
          </span>
        </li>
      ))}
    </ul>
  );
}
